import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Breadcrumb, { Crumb } from "../../../../components/common/Breadcrumbs";
import AuditableInfoButton from "../../../../components/common/form/AuditableInfoButton";
import { useCreateCodeMutation, useDeleteCodeByIdMutation, useGetCodeByIdQuery, useUpdateCodeMutation } from "../codeApi";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import { Routes as AppRoutes } from "../../../../routes";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import TextAreaField from "../../../../components/common/form/TextAreaField";
import ModalButton from "../../../../components/common/form/ModalButton";
import IconButton from "../../../../components/common/form/IconButton";
import { FaTrash } from "react-icons/fa6";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isReadOnly?: boolean;
}

const CodeFormField: FC<Props> = ({ crumbs, title, btnText, isReadOnly }) => {
    let { codeId } = useParams();
    const { data: codeData } = useGetCodeByIdQuery(codeId);
    const [createCodeApi, createCodeApiResponse] = useCreateCodeMutation();
    const [updatedCodeApi, updateCodeApiResponse] = useUpdateCodeMutation();
    const [code, setCode] = useState<any>({ name: "", code: "" });
    const [deleteCode, { isLoading }] = useDeleteCodeByIdMutation();

    const navigate = useNavigate();

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const updatedData = { ...code };

        const createOrUpdateCode = async () => {
            if (btnText === "Create") {
                return await createCodeApi(updatedData).unwrap();
            } else {
                return await updatedCodeApi({
                    id: codeId,
                    requestBody: updatedData,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateCode, (response: any) => {
            if (response?.success || response?.data?.success) {
                setCode({ name: "", code: "" });
                navigate(AppRoutes.Codes.path);
            }
        });
    };
    const handleDelete = async () => {
        if (codeId) {
            await deleteCode(codeId).unwrap();
            navigate(AppRoutes.Codes.path);
        }
    };

    const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { name: string; value: any }) => {
        const { name, value } = "target" in e ? e.target : e;
        setCode((prev: any) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (codeData && btnText !== "Create") {
            setCode({ ...codeData });
        }
    }, [codeData, btnText]);

    return (
        <div className="px-4 py-2">
            <div className="flex justify-between mb-3">
                <Breadcrumb crumbs={crumbs} />
                <div className={"justify-between sm:block md:flex lg:flex"}>

                    {codeId && hasPermission(["LOGIC-D"]) && (
                        <>
                            <ModalButton
                                triggerButton={
                                    <IconButton
                                        icon={<FaTrash />}
                                        title={"Delete Code"}
                                        hasBackground={true}
                                        className="mr-2"
                                    />
                                }
                                modal={
                                    <ConfirmationModal
                                        show={true}
                                        setShow={() => {
                                        }}
                                        title={"Delete Code"}
                                        bodyMessage={"Are you sure you want to delete this Code?"}
                                        onSubmit={handleDelete}
                                        isLoading={isLoading}
                                    />
                                }
                            />
                            {
                                hasPermission(["AUDIT-V"]) &&
                                <AuditableInfoButton obj={codeData} />}
                        </>
                    )}
                </div>
            </div>
            <div className="flex justify-center mt-3">
                <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                    <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">
                        {title}
                    </h3>

                    <Form onSubmit={handleSubmit} fields={
                        <>
                            <InputField
                                name="name"
                                label="Name"
                                onChange={handleOnChange}
                                value={code?.name || ""}
                                required
                                placeholder="Enter Name..."
                                disabled={isReadOnly}
                            />
                            <TextAreaField
                                name="code"
                                label="Code"
                                rows={8}
                                onChange={handleOnChange}
                                value={code?.code || ""}
                                required
                                placeholder="Enter code..."
                                disabled={isReadOnly}
                            />

                            {!isReadOnly &&


                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray buttons={[
                                                {
                                                    buttonProps: {
                                                        btnText: "Cancel",
                                                        onClick: () => navigate(AppRoutes.Codes.path),
                                                        type: "cancel"
                                                    },
                                                    buttonType: "button"
                                                },
                                                {
                                                    buttonProps: {
                                                        btnText: "Submit",
                                                        type: "submit",
                                                        isLoading: (createCodeApiResponse.isLoading || updateCodeApiResponse.isLoading),
                                                        isVisible: hasPermission(["CANNOTE-U"])
                                                    },
                                                    buttonType: "button"
                                                }
                                            ]} align="center" gap={2} />
                                        </div>
                                    </div>
                                </div>}
                        </>
                    } />
                </div>
            </div>
        </div>
    );
};

export default CodeFormField;
